(function () {
  window.PdpCamelliaVideo = window.PdpCamelliaVideo || {};

  if (window.PdpCamelliaVideo.loaded) {
    return;
  }

  window.PdpCamelliaVideo.loaded = true;

  class PdpCamelliaVideo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.video = this.querySelector('[data-camellia-video]');
      this.controls = this.querySelector('[data-camellia-video-controls]');
      this.control = this.querySelector('[data-camellia-video-control]');
      this.controlLabel = this.querySelector(
        '[data-camellia-video-control-label]'
      );
      this.muteControl = this.querySelector('[data-camellia-video-mute]');
      this.muteLabel = this.querySelector('[data-camellia-video-mute-label]');

      if (!this.video || !this.controls || !this.control || !this.muteControl) {
        return;
      }

      this.initialized = true;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.loopEnabled = this.dataset.loop === 'true';
      this.userPaused = false;
      this.userAdjustedMute = false;
      this.manualPlaybackStarted = false;
      this.resumeAfterVisibility = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.handleControlClick = this.handleControlClick.bind(this);
      this.handleMuteClick = this.handleMuteClick.bind(this);
      this.handlePlay = this.handlePlay.bind(this);
      this.handlePause = this.handlePause.bind(this);
      this.handleLoadedData = this.handleLoadedData.bind(this);
      this.handleError = this.handleError.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);

      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.loop = this.loopEnabled;
      this.updateMutedState();
      this.controls.hidden = false;

      this.control.addEventListener('click', this.handleControlClick);
      this.muteControl.addEventListener('click', this.handleMuteClick);
      this.video.addEventListener('play', this.handlePlay);
      this.video.addEventListener('pause', this.handlePause);
      this.video.addEventListener('loadeddata', this.handleLoadedData);
      this.video.addEventListener('error', this.handleError);
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );

      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.handleMotionChange);
      } else {
        this.motionQuery.addListener(this.handleMotionChange);
      }

      if (this.video.readyState >= 2) {
        this.handleLoadedData();
      }

      if (this.video.error) {
        this.handleError();
        return;
      }

      if (this.motionQuery.matches) {
        this.applyReducedMotion();
      } else if (this.autoplayEnabled) {
        this.attemptPlayback(false);
      } else if (this.video.paused) {
        this.setState('paused');
      } else {
        this.setState('playing');
      }
    }

    disconnectedCallback() {
      if (!this.initialized) {
        return;
      }

      this.control.removeEventListener('click', this.handleControlClick);
      this.muteControl.removeEventListener('click', this.handleMuteClick);
      this.video.removeEventListener('play', this.handlePlay);
      this.video.removeEventListener('pause', this.handlePause);
      this.video.removeEventListener('loadeddata', this.handleLoadedData);
      this.video.removeEventListener('error', this.handleError);
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );

      if (this.motionQuery.addEventListener) {
        this.motionQuery.removeEventListener('change', this.handleMotionChange);
      } else {
        this.motionQuery.removeListener(this.handleMotionChange);
      }

      this.initialized = false;
    }

    handleControlClick() {
      if (this.video.paused) {
        this.userPaused = false;
        this.manualPlaybackStarted = true;
        this.video.loop = this.loopEnabled;
        this.attemptPlayback(true);
      } else {
        this.userPaused = true;
        this.resumeAfterVisibility = false;
        this.video.pause();
      }
    }

    handleMuteClick() {
      this.userAdjustedMute = true;
      this.video.muted = !this.video.muted;
      this.video.defaultMuted = this.video.muted;
      this.updateMutedState();
    }

    handlePlay() {
      this.setState('playing');
    }

    handlePause() {
      if (this.dataset.state !== 'error') {
        this.setState('paused');
      }
    }

    handleLoadedData() {
      this.classList.add('has-frame');
    }

    handleError() {
      this.classList.add('is-error');
      this.setState('error');
      this.control.disabled = true;
      this.muteControl.disabled = true;
      this.controls.hidden = true;
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.resumeAfterVisibility = !this.video.paused && !this.userPaused;

        if (!this.video.paused) {
          this.video.pause();
        }

        return;
      }

      const motionAllowsPlayback =
        !this.motionQuery.matches || this.manualPlaybackStarted;

      if (
        this.resumeAfterVisibility &&
        !this.userPaused &&
        motionAllowsPlayback
      ) {
        this.resumeAfterVisibility = false;
        this.attemptPlayback(false);
      }
    }

    handleMotionChange(event) {
      if (event.matches) {
        this.applyReducedMotion();
        return;
      }

      this.video.loop = this.loopEnabled;

      if (this.autoplayEnabled && !this.userPaused && !document.hidden) {
        this.attemptPlayback(false);
      }
    }

    applyReducedMotion() {
      this.video.autoplay = false;
      this.video.removeAttribute('autoplay');
      this.video.loop = false;
      this.resumeAfterVisibility = false;

      if (!this.video.paused) {
        this.video.pause();
      } else {
        this.setState('paused');
      }
    }

    attemptPlayback(userInitiated) {
      if (
        this.motionQuery.matches &&
        !userInitiated &&
        !this.manualPlaybackStarted
      ) {
        this.setState('paused');
        return;
      }

      if (!this.userAdjustedMute) {
        this.video.muted = true;
        this.video.defaultMuted = true;
        this.updateMutedState();
      }

      const playPromise = this.video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          if (!userInitiated) {
            this.setState('blocked');
          } else {
            this.setState('paused');
          }
        });
      }
    }

    updateMutedState() {
      this.dataset.muted = String(this.video.muted);

      if (this.muteLabel) {
        this.muteLabel.textContent = this.video.muted
          ? 'Unmute video'
          : 'Mute video';
      }
    }

    setState(state) {
      this.dataset.state = state;
      this.classList.toggle('is-error', state === 'error');

      if (!this.controlLabel) {
        return;
      }

      this.controlLabel.textContent =
        state === 'playing' ? 'Pause video' : 'Play video';
    }
  }

  if (!customElements.get('pdp-camellia-video')) {
    customElements.define('pdp-camellia-video', PdpCamelliaVideo);
  }
})();